import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowUpDown, ArrowDown, ArrowUp, Search, ChevronLeft, ChevronRight } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import { SkeletonTable } from "@/components/Skeleton";

/**
 * DataTable — premium reusable table with search, sortable columns,
 * pagination, empty & loading states.
 */
export default function DataTable({
  columns,
  fetcher,
  emptyProps,
  toolbar,
  defaultSort = "",
  defaultOrder = "desc",
  pageSize = 10,
  refreshKey = 0,
  onRowClick,
  rowKey = "id",
  searchPlaceholder = "Ara…",
  rowTestId,
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState(defaultSort);
  const [order, setOrder] = useState(defaultOrder);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetcher({ page, page_size: pageSize, search: debouncedSearch, sort, order })
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setData({ items: [], total: 0 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, sort, order, refreshKey]);

  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));
  const showEmpty = !loading && (data.items || []).length === 0;

  const toggleSort = (key) => {
    if (sort === key) setOrder(order === "asc" ? "desc" : "asc");
    else { setSort(key); setOrder("desc"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex-1 flex flex-wrap items-center gap-2 min-w-0">
          {toolbar}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 rounded-lg bg-background"
            data-testid="dt-search"
          />
        </div>
      </div>

      {loading ? (
        <SkeletonTable rows={5} cols={columns.length} />
      ) : showEmpty ? (
        <EmptyState {...emptyProps} />
      ) : (
        <div className="border rounded-xl overflow-hidden bg-background shadow-elev-1 animate-fade-rise">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40 border-b">
                  {columns.map((c) => (
                    <TableHead key={c.key} className={`h-11 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold ${c.className || ""}`}>
                      {c.sortable ? (
                        <button
                          onClick={() => toggleSort(c.key)}
                          className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
                          data-testid={`sort-${c.key}`}
                        >
                          {c.label}
                          {sort === c.key ? (
                            order === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                        </button>
                      ) : c.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((row, idx) => (
                  <TableRow
                    key={row[rowKey] || idx}
                    data-testid={rowTestId ? `${rowTestId}-${row[rowKey] || idx}` : undefined}
                    className={`transition-colors ${onRowClick ? "cursor-pointer hover:bg-muted/40" : "hover:bg-muted/30"}`}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((c) => (
                      <TableCell key={c.key} className={`py-3 ${c.className || ""}`}>
                        {c.render ? c.render(row) : row[c.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {data.total > pageSize && (
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
          <div className="font-mono">
            {(data.page - 1) * pageSize + 1}–{Math.min(data.page * pageSize, data.total)} / {data.total}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-lg" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="dt-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="font-mono text-xs px-2">{page} / {totalPages}</div>
            <Button variant="outline" size="icon" className="h-8 w-8 rounded-lg" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="dt-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
